import { useRef, type ReactNode } from "react";
import { Settings, Key } from "lucide-react";
import { cn } from "@/lib/utils";
import { useScrollSpotlight } from "../scroll-spotlight";

/* -------------------------------------------------------------------------- */
/*  Shared primitives                                                         */
/* -------------------------------------------------------------------------- */

function NumberedInstruction({ step, title, children }: { step: number; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);
  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex flex-col items-center">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">{step}</div>
        <div className="mt-3 w-px flex-1 bg-white/[0.06]" />
      </div>
      <div className="min-w-0 flex-1 pb-28">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function NumberedInstructionLast({ step, title, children }: { step: number; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);
  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">{step}</div>
      <div className="min-w-0 flex-1 pb-12">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, title, description, accentClass }: { icon: React.ComponentType<{ className?: string }>; title: string; description: string; accentClass: string }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/[0.06]">
        <Icon className={cn("h-5 w-5", accentClass)} />
      </div>
      <p className="mt-3 text-sm font-semibold text-foreground">{title}</p>
      <p className="mt-1.5 text-sm leading-6 text-muted-foreground">{description}</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  FogBugz Setup Step — shared across all FogBugz guides                     */
/* -------------------------------------------------------------------------- */

export function FogBugzSetupStep() {
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="Check if FogBugz is configured">
        <p className="text-sm leading-7 text-muted-foreground">
          Before using any FogBugz feature, you need to connect your FogBugz account.
          Open <strong className="text-foreground/90">Settings</strong> in the bottom
          navigation bar and look for the{" "}
          <strong className="text-foreground/90">FogBugz Integration</strong> section.
        </p>
        <p className="text-sm leading-7 text-muted-foreground">
          If you see a green checkmark and{" "}
          <strong className="text-foreground/90">API key configured</strong>, {"you're"}{" "}
          all set — skip to the next step.
        </p>
        <p className="text-sm leading-7 text-muted-foreground">
          If you see an <strong className="text-foreground/90">X</strong> and{" "}
          <strong className="text-foreground/90">No API key configured</strong>, follow the
          steps below.
        </p>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="Get your FogBugz API key">
        <p className="text-sm leading-7 text-muted-foreground">
          Log in to your FogBugz instance and generate a personal API token:
        </p>
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-amber-400" />
            Go to your FogBugz instance (e.g.{" "}
            <span className="font-mono text-foreground/70">
              yourteam.fogbugz.com
            </span>
            )
          </p>
          <p className="text-sm text-muted-foreground">
            <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-amber-400" />
            Navigate to your user avatar and select{" "}
            <strong className="text-foreground/80">User Options</strong>
          </p>
          <p className="text-sm text-muted-foreground">
            <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-amber-400" />
            Find the <strong className="text-foreground/80">API Token</strong> section
          </p>
          <p className="text-sm text-muted-foreground">
            <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-amber-400" />
            Copy the token — you will paste it in the next step
          </p>
        </div>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="Save your API key in SpecTree">
        <p className="text-sm leading-7 text-muted-foreground">
          Back in SpecTree, go to{" "}
          <strong className="text-foreground/90">Settings</strong> and find the{" "}
          <strong className="text-foreground/90">FogBugz Integration</strong> card:
        </p>
        <div className="grid gap-3 md:grid-cols-2">
          <InfoCard
            icon={Key}
            title="Paste your API key"
            description="Enter your FogBugz API token in the text field and click Save API Key. The key is encrypted and stored securely."
            accentClass="text-amber-400"
          />
          <InfoCard
            icon={Settings}
            title="Verify connection"
            description="Once saved, the status changes to a green checkmark with API key configured. You can now use all FogBugz features."
            accentClass="text-emerald-400"
          />
        </div>
        <p className="mt-3 text-sm leading-7 text-muted-foreground">
          You only need to do this once. The key persists across sessions. You can update
          or clear it any time from the same Settings page.
        </p>
      </NumberedInstructionLast>
    </div>
  );
}
